/**
 * Installs browser-side global error handlers that ship errors to
 * /api/public/logs/error. Safe to import from route/component code.
 */
import { supabase } from "@/integrations/supabase/client";
import {
  isInjectedMetaBridgeError,
  noteSuppressedInjectedError,
  sanitizeTelemetryUrl,
} from "@/lib/sentry-filter";

let installed = false;
const recent = new Map<string, number>();
const DEDUPE_MS = 10_000;


function shouldSend(key: string): boolean {
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  recent.set(key, now);
  if (recent.size > 100) {
    for (const [k, t] of recent) if (now - t > DEDUPE_MS) recent.delete(k);
  }
  return true;
}

async function post(payload: Record<string, unknown>) {
  try {
    await fetch("/api/public/logs/error", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* ignore */
  }
}

export function installClientErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  let userId: string | null = null;
  supabase.auth.getSession().then(({ data }) => {
    userId = data.session?.user?.id ?? null;
  }).catch(() => {});
  supabase.auth.onAuthStateChange((_e, s) => {
    userId = s?.user?.id ?? null;
  });

  const isExternalMetaNoise = (message: string, stack: string | null, extraFrames: (string | null | undefined)[]) =>
    isInjectedMetaBridgeError({
      message,
      frames: [stack, ...extraFrames],
      userAgent: navigator.userAgent,
    });

  window.addEventListener("error", (event) => {
    const err = event.error;
    const message = err?.message ?? event.message ?? "window.error";
    const stack = err?.stack ?? null;
    if (isExternalMetaNoise(message, stack, [event.filename])) {
      noteSuppressedInjectedError();
      return;
    }
    const key = `${message}::${stack?.slice(0, 200) ?? ""}`;
    if (!shouldSend(key)) return;
    post({
      message,
      stack,
      url: sanitizeTelemetryUrl(window.location.href),
      userId,
      context: { type: "window.error", filename: event.filename, lineno: event.lineno, colno: event.colno },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    const message = reason?.message ?? (typeof reason === "string" ? reason : "unhandledrejection");
    const stack = reason?.stack ?? null;
    if (isExternalMetaNoise(message, stack, [])) {
      noteSuppressedInjectedError();
      return;
    }
    const key = `${message}::${stack?.slice(0, 200) ?? ""}`;
    if (!shouldSend(key)) return;
    post({
      message,
      stack,
      url: sanitizeTelemetryUrl(window.location.href),
      userId,
      context: { type: "unhandledrejection" },
    });
  });

}
